import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, MapPin, Phone, Send } from "lucide-react";
import { FadeUp, Eyebrow } from "./Reveal";
import { LogoMark } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENQUIRY_OPTIONS = [
  { value: "funder", label: "Funder / Investor" },
  { value: "supplier", label: "Supplier" },
  { value: "research", label: "Research Partner" },
  { value: "general", label: "General Enquiry" },
];

const inputCls =
  "w-full rounded-xl border border-[#1E3A2B]/18 bg-white/70 px-4 py-3.5 text-[15px] text-[#121512] placeholder:text-[#5A665D]/50 outline-none transition-colors duration-300 focus:border-[#1E3A2B] focus:bg-white";

const FOUNDER_ROWS = [
  { icon: Phone, label: "+64 21 148 3512", href: "tel:+64211483512", testid: "contact-phone-link" },
  { icon: Mail, label: "mikaelacomerford.business@gmail.com", href: "mailto:mikaelacomerford.business@gmail.com", testid: "contact-email-link" },
  { icon: MapPin, label: "Auckland & Kāwhia, New Zealand", href: null, testid: "contact-location" },
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", enquiry_type: "funder", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry sent — ngā mihi. We'll be in touch shortly.", {
        description: "Your message has been delivered to Aotea Biotech.",
      });
      setForm({ name: "", email: "", organisation: "", enquiry_type: "funder", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Couldn't send your enquiry", {
        description: typeof detail === "string" ? detail : "Please try again, or email us directly.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative bg-[#FBF9F4] py-28 sm:py-40" data-testid="contact-section">
      <div className="max-w-[1440px] mx-auto px-6 sm:px-10">
        <FadeUp>
          <Eyebrow>04 — Funders &amp; Suppliers</Eyebrow>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h2 className="font-serif-brand text-4xl sm:text-5xl lg:text-6xl text-[#121512] leading-[1.05] tracking-tight mt-6 max-w-3xl">
            Let's kōrero about <span className="italic text-[#1E3A2B]">what's possible.</span>
          </h2>
        </FadeUp>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mt-14 sm:mt-20">
          <FadeUp className="lg:col-span-5" delay={0.15}>
            <div className="relative h-full overflow-hidden rounded-3xl bg-[#121512] p-9 sm:p-12 flex flex-col justify-between min-h-[480px]" data-testid="founder-card">
              <div
                className="absolute inset-0 opacity-[0.14] bg-cover bg-center"
                style={{
                  backgroundImage:
                    "url(https://images.unsplash.com/photo-1527138332241-b3eed4958d40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxuZXclMjB6ZWFsYW5kJTIwcm9sbGluZyUyMGdyZWVuJTIwaGlsbHMlMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzkzNzQzMDYyfDA&ixlib=rb-4.1.0&q=85)",
                }}
              />
              <div className="relative">
                <LogoMark showEst className="text-[#FBF9F4]" />
                <p className="font-mono-brand text-[10px] uppercase tracking-[0.3em] text-[#D4A359] mt-8">Founder &amp; Director</p>
                <h3 className="font-serif-brand text-4xl sm:text-5xl text-[#FBF9F4] mt-3 leading-tight">Mikaela<br />Comerford</h3>
                <p className="text-sm text-[#D1D8D2]/70 leading-relaxed mt-5 max-w-xs">
                  Background in legal, risk, compliance, operations and HR — leading Aotea
                  Biotech from foundations to licensed cultivation.
                </p>
              </div>
              <div className="relative space-y-4 mt-12">
                {FOUNDER_ROWS.map((row) => {
                  const Inner = (
                    <>
                      <row.icon className="w-4 h-4 text-[#D4A359] shrink-0" />
                      <span className="text-sm sm:text-[15px] text-[#FBF9F4]/90">{row.label}</span>
                    </>
                  );
                  return row.href ? (
                    <a
                      key={row.testid}
                      href={row.href}
                      data-testid={row.testid}
                      className="flex items-center gap-3.5 rounded-xl border border-[#FBF9F4]/10 bg-[#FBF9F4]/5 px-4 py-3.5 transition-colors duration-300 hover:border-[#D4A359]/50 hover:bg-[#FBF9F4]/10"
                    >
                      {Inner}
                    </a>
                  ) : (
                    <div
                      key={row.testid}
                      data-testid={row.testid}
                      className="flex items-center gap-3.5 rounded-xl border border-[#FBF9F4]/10 bg-[#FBF9F4]/5 px-4 py-3.5"
                    >
                      {Inner}
                    </div>
                  );
                })}
              </div>
            </div>
          </FadeUp>

          <FadeUp className="lg:col-span-7" delay={0.25}>
            <form onSubmit={submit} className="rounded-3xl border border-[#1E3A2B]/12 bg-[#F3EFE6] p-8 sm:p-12" data-testid="contact-form">
              <p className="font-mono-brand text-[10px] uppercase tracking-[0.28em] text-[#3B5E4A] mb-8">
                Enquiries from funders, investors &amp; suppliers
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="enquiry-name" className="block font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#5A665D] mb-2">Name *</label>
                  <input id="enquiry-name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="enquiry-email" className="block font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#5A665D] mb-2">Email *</label>
                  <input id="enquiry-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.co.nz" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="enquiry-org" className="block font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#5A665D] mb-2">Organisation</label>
                  <input id="enquiry-org" data-testid="contact-organisation-input" value={form.organisation} onChange={set("organisation")} placeholder="Company or trust (optional)" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="enquiry-type" className="block font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#5A665D] mb-2">I am a *</label>
                  <select id="enquiry-type" data-testid="contact-type-select" value={form.enquiry_type} onChange={set("enquiry_type")} className={`${inputCls} appearance-none cursor-pointer`}>
                    {ENQUIRY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="enquiry-message" className="block font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#5A665D] mb-2">Message *</label>
                  <textarea id="enquiry-message" data-testid="contact-message-input" required minLength={10} rows={5} value={form.message} onChange={set("message")} placeholder="Tell us a little about your interest — funding, supply, research partnership…" className={`${inputCls} resize-none`} />
                </div>
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="group mt-8 flex w-full sm:w-auto items-center justify-center gap-3 rounded-full bg-[#1E3A2B] px-9 py-4 text-sm font-semibold text-[#FBF9F4] transition-colors duration-300 hover:bg-[#2C4A38] disabled:opacity-60"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Send Enquiry
                    <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
              <p className="mt-5 text-xs text-[#5A665D]/80 leading-relaxed">
                Your enquiry goes straight to Mikaela's inbox. We never share your details.
              </p>
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};
